// ---------------------------------------------------------------------------
// Build a `ClassifiedRoute` carrier from a Google Directions payload.
// ---------------------------------------------------------------------------
// The route list now guarantees one slot is ALWAYS the Google Maps route
// (so users can compare every Pleasant alternative against the literal
// route Google would give them). The simplest way to render that slot is
// to reuse the existing <RouteCard /> — which means we need a
// `ClassifiedRoute` carrier whose `route` is the Google polyline + ETA
// and whose features look reasonable enough that the card and downstream
// "+X min" math don't crash.
//
// We start from the engine's own "fastest" classified route as a feature
// fallback (so explanation chips, evidence lists, and dimension scores
// stay non-empty), then override:
//
//   • `route.coordinates`  → Google's polyline (drives the map polyline
//                              when this card is selected)
//   • `route.distance`     → Google distance (m)
//   • `route.duration`     → Google duration (s)
//   • `features.highwayPct`→ true highway% from Google's own steps
//   • `classification`     → "fastest" (we keep the slot identity so all
//                              existing baseline / selection logic still
//                              works; the RouteCard re-labels it
//                              "Google Maps" via `META.fastest` overrides)
//   • `explanation`        → a single honest sentence — never our scoring
//                              prose, since this isn't our route
//
// We intentionally do NOT recompute scores or features against Google's
// polyline (we'd need the full OSM enrichment pipeline for that).
// Numeric scores are inherited and exist only to keep the type happy;
// the card's display path doesn't surface them for the Google slot.
// ---------------------------------------------------------------------------

import type { GoogleRoute } from "@/api/googleDirections";
import { tagRouteSource } from "@/lib/routeDiagnostics";
import { highwayPctOf } from "@/api/googleDirections";
import type { RouteStep } from "@/api/routing";
import type { ClassifiedRoute } from "@/utils/scoring";

/** A neutral, well-typed `FeatureSummary` used when we don't have an
 *  engine fastest route to inherit from (rare — happens on the first
 *  render before scoring resolves). All numeric fields are zero so no
 *  downstream "+X min" or % math produces NaN. */
const EMPTY_FEATURES: ClassifiedRoute["features"] = {
  parks: 0,
  cafes: 0,
  shops: 0,
  landmarks: 0,
  waterPct: 0,
  highwayPct: 0,
  majorRoadPct: 0,
  residentialPct: 0,
  bikeLaneKm: 0,
  sidewalkKm: 0,
  noSidewalkKm: 0,
};

const EMPTY_SCORES: ClassifiedRoute["scores"] = {
  preferenceMatch: 0,
  scenic: 0,
  efficiency: 0,
  calm: 0,
  community: 0,
  pleasant: 0,
  profileMatch: 0,
};

/** True when this carrier was synthesized from a Google Directions
 *  response. The RouteCard reads it to swap label/subtitle and skip the
 *  epic-highway promotion path. */
export const GOOGLE_CARRIER_FLAG = "__googleCarrier" as const;

export interface GoogleCarrierRoute extends ClassifiedRoute {
  [GOOGLE_CARRIER_FLAG]: true;
}

export const isGoogleCarrier = (
  r: ClassifiedRoute | undefined | null,
): r is GoogleCarrierRoute =>
  !!r && (r as Partial<GoogleCarrierRoute>)[GOOGLE_CARRIER_FLAG] === true;

/** Build the carrier. `inherit` is the engine's "fastest" entry when
 *  available — its features/scores/dimensions/evidence are reused so the
 *  card has reasonable data to render even though the route geometry is
 *  Google's. */
/**
 * Map Google's maneuver string ("turn-left", "roundabout-right",
 * "ramp-left", "fork-right", "uturn-left", "merge", "straight", …) onto
 * the Mapbox-style maneuverType/maneuverModifier the rest of the app
 * reads. Unknown/absent strings return {} and the banner falls back to
 * instruction text.
 */
export function googleManeuverFields(
  maneuver?: string,
): { maneuverType?: string; maneuverModifier?: string } {
  if (!maneuver) return {};
  const g = maneuver.toLowerCase().trim();
  const side = g.includes("left") ? "left" : g.includes("right") ? "right" : undefined;

  if (g.startsWith("uturn")) return { maneuverType: "turn", maneuverModifier: "uturn" };
  if (g.startsWith("roundabout")) return { maneuverType: "roundabout", maneuverModifier: side };
  if (g.startsWith("ramp")) return { maneuverType: "on ramp", maneuverModifier: side };
  if (g.startsWith("fork")) return { maneuverType: "fork", maneuverModifier: side ? `slight ${side}` : undefined };
  if (g.startsWith("merge")) return { maneuverType: "merge", maneuverModifier: side ? `slight ${side}` : undefined };
  if (g.startsWith("keep")) return { maneuverType: "fork", maneuverModifier: side ? `slight ${side}` : undefined };
  if (g === "straight") return { maneuverType: "continue", maneuverModifier: "straight" };
  if (g.startsWith("turn-sharp")) return { maneuverType: "turn", maneuverModifier: side ? `sharp ${side}` : undefined };
  if (g.startsWith("turn-slight")) return { maneuverType: "turn", maneuverModifier: side ? `slight ${side}` : undefined };
  if (g.startsWith("turn")) return { maneuverType: "turn", maneuverModifier: side };
  return {};
}

export function buildGoogleCarrier(
  google: GoogleRoute,
  inherit: ClassifiedRoute | undefined,
): GoogleCarrierRoute {
  const baseFeatures = inherit?.features ?? EMPTY_FEATURES;
  const googleHighwayPct = highwayPctOf(google);

  // Turn-by-turn steps built from GOOGLE'S OWN step data, so the directions
  // match Google's polyline. Previously the carrier inherited the engine
  // "fastest" route's steps — a DIFFERENT road. Navigating the Google card
  // then ran the turn-by-turn engine against mismatched step boundaries, so
  // it never advanced and sat stuck on one huge highway step. Real tester
  // report: directions "not working ... it just said drive for 25 miles."
  // Google gives instruction + distance per step; per-step duration isn't
  // surfaced, so we apportion it from the total by distance share.
  const totalDistM = google.distanceM > 0 ? google.distanceM : 1;
  const carrierSteps: RouteStep[] = (google.steps ?? []).map((s) => ({
    instruction:
      (s.instruction ?? "").replace(/<[^>]*>/g, "").trim() || "Continue",
    distance: s.distanceM,
    duration: google.durationS * (s.distanceM / totalDistM),
    // Structured maneuver fields from Google's maneuver string, so the
    // banner icon comes from data, not prose regex (the desync fix).
    ...googleManeuverFields(s.maneuver),
  }));

  const carrier: GoogleCarrierRoute = {
    // --- Route geometry (Google's actual polyline & ETA) ---
    route: {
      ...(inherit?.route ?? {
        steps: [],
      }),
      coordinates: google.coordinates,
      distance: google.distanceM,
      duration: google.durationS,
      // Steps must describe GOOGLE's road, not the inherited engine route.
      steps: carrierSteps,
      // The inherited engine route also carried stop indices for a
      // different geometry — drop them.
      stopStepIndices: [],
      // The engine's epic-highway labels don't apply to Google's route —
      // strip them so the RouteCard doesn't promote this slot to a
      // scenic-highway label.
      epicHighwayMeters: 0,
      epicHighwayLabels: [],
    },
    features: { ...baseFeatures, highwayPct: googleHighwayPct },
    scores: inherit?.scores ?? EMPTY_SCORES,
    dimensions: inherit?.dimensions ?? ({} as ClassifiedRoute["dimensions"]),
    evidence: [],
    // (Morgan: "we don't want a mention of google at all") — brand-free copy.
    // This card IS the comparison baseline; its identity is "the direct way",
    // not the engine that produced it.
    explanation: "The direct route — the fastest way there.",
    classification: "fastest",
    sameAsFastest: false,
    [GOOGLE_CARRIER_FLAG]: true,
  };
  // RELEASE SPEC item 1 (Aug 11): the canonical Fastest card must keep a
  // source. The `...inherit?.route` spread above dropped the (then
  // non-enumerable) tag, so the card that stands in for Direct logged
  // src=unknown while wearing labels computed from another route's
  // features. Name this geometry for what it is.
  tagRouteSource(carrier.route, "google-direct");
  return carrier;
}
